import { useRef, useCallback, useEffect } from "react";
import Swal from "sweetalert2";
import { getSocket } from "../../services/socket";
import useProgressLog from "./useProgressLog";

const useProgressModal = ({
  socketEvents = { progress: "progreso", error: "error", success: null },
  modalTitle = "Procesando...",
  wide = false,
  showLogs = false,
}) => {
  const modalVisible = useRef(false);
  const socket = getSocket();
  const { logs, log, reset, logsRef } = useProgressLog();

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  const start = useCallback(() => {
    if (!socket) {
      console.warn("⛔ Socket no conectado.");
      return;
    }

    if (modalVisible.current) {
      console.warn("⚠️ El modal ya está abierto.");
      return;
    }

    reset();

    let logsHtml = "";
    if (showLogs) {
      logsHtml = `
        <div
          id="terminal-container"
          style="width:100%; height:200px;overflow-y:auto;background-color:#efefef;color:#2b2a2a;
                font-family:monospace;font-size:10px;padding:6px;border-radius:6px;
                border:1px solid #333;margin-top:10px;text-align:left;"
        >
          Inicializando...
        </div>
      `;
    }

    Swal.fire({
      title: modalTitle,
      html: `<p id="progress-msg">Procesando...</p> ${logsHtml}`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      ...(wide ? { width: "70%" } : {}),
      didOpen: () => {
        Swal.showLoading();
        modalVisible.current = true;
      },
      willClose: () => {
        modalVisible.current = false;
      },
    });

    const updateUI = (msg) => {
      log(msg); // esto ya actualiza logsRef y setLogs

      if (showLogs) {
        const el = document.getElementById("terminal-container");
        if (el) {
          const currentLogs = logsRef.current; // ✅ usamos el valor actualizado
          el.innerHTML = currentLogs.map((m) => `<div>${m}</div>`).join("");
          el.scrollTop = el.scrollHeight;
        }
      }

      const el_prog = document.getElementById("progress-msg");
      if (el_prog) el_prog.innerText = msg;
    };

    if (socketEvents.progress) {
      socket?.on(`${socketEvents.progress}`, (msg) => updateUI(msg));
    }
    if (socketEvents.error) {
      socket?.on(`${socketEvents.error}`, (msg) => updateUI(`❌ ${msg}`, true));
    }

    if (socketEvents.success) {
      socket?.on(`${socketEvents.success}`, (msg) => {
        Swal.update({
          title: "Completado",
          icon: "success",
          html: `<p id="progress-msg"></p>${logsHtml}`,
          showConfirmButton: true,
        });
        Swal.hideLoading();
        updateUI(`✅ ${msg}`);
      });
    }
  }, [socket, socketEvents, modalTitle, log, reset, logsRef]);

  const stop = useCallback(() => {
    Object.values(socketEvents).forEach((evt) => {
      if (evt) socket?.off(evt);
    });

    Swal.close(); // cerrar modal manualmente
    modalVisible.current = false;
  }, [socketEvents]);

  return { start, stop };
};

export default useProgressModal;
